/**
 * Dashboard /history tab — ledger entries with category + period filters,
 * cursor-paged loads, in-DOM category filter without re-fetch.
 *
 * Originally lines 776-1145 of `routes/dashboard.ts`. Phase 2.2.
 */
import { escapeHtml } from '../../utils';
import * as api from '../../api';
import { showToast } from '../../toast';
import { t } from '../../i18n';
import { fmtData, fmtDate, fmtMoney } from '../../utils/format';

export const HISTORY_CATEGORIES = ['all', 'topup', 'usage', 'refund', 'bonus'] as const;
export type HistoryCategory = typeof HISTORY_CATEGORIES[number];

export function getHistoryCategory(rawType: string): Exclude<HistoryCategory, 'all'> | null {
  if (rawType === 'topup') return 'topup';
  if (rawType === 'usage' || rawType === 'esim-debit') return 'usage';
  if (rawType === 'topup-refund' || rawType === 'refund') return 'refund';
  if (rawType === 'promo-bonus' || rawType === 'trial-credit') return 'bonus';
  return null;  // adjustment + future types — show only on 'all'
}

export type HistoryPeriod = '1d' | '7d' | '14d' | '30d' | 'all';

export function historyPanel(): string {
  // Two parallel filter rows + table + Load more button.
  //   • Period pills (1d/7d/14d/30d/All) — re-fetches from server
  //     because the page might have many entries we never loaded.
  //   • Category pills (Все/Top-ups/Usage/Refunds/Bonuses) — pure
  //     client-side toggle on already-loaded rows.
  //   • "Load more" button at the bottom — uses cursor returned by
  //     /api/me/history. Hides itself when the server reports
  //     `nextCursor: null`.
  // Grammarly / LanguageTool / similar browser extensions inject grey
  // strikethrough overlays on these short-text pills (operator reported
  // a thin grey line through "All (6)" 2026-05-08). Opt out at the
  // attribute level so extensions skip them.
  const noGramm = 'data-gramm="false" data-gramm_editor="false" data-lt-tmp="0" spellcheck="false"';
  const catPill = (cat: HistoryCategory, label: string): string =>
    `<button class="dash-history__filter${cat === 'all' ? ' is-active' : ''}" type="button" data-history-filter="${cat}" ${noGramm}>
       ${escapeHtml(label)} <span class="dash-history__filter-count" data-history-filter-count="${cat}"></span>
     </button>`;
  const periodPill = (p: HistoryPeriod, label: string): string =>
    `<button class="dash-history__filter${p === 'all' ? ' is-active' : ''}" type="button" data-history-period="${p}" ${noGramm}>
       ${escapeHtml(label)}
     </button>`;
  return `
<h2 class="dash-h2">${escapeHtml(t('dash.history.title'))}</h2>
<p class="dash-sub" style="margin-bottom:16px;" data-i18n="dash.history.subPerEsim">${escapeHtml(t('dash.history.subPerEsim'))}</p>
<div class="dash-history__controls">
  <div class="dash-history__filters dash-history__filters--period" data-history-period-filters>
    ${periodPill('1d',  t('dash.history.period1d'))}
    ${periodPill('7d',  t('dash.history.period7d'))}
    ${periodPill('14d', t('dash.history.period14d'))}
    ${periodPill('30d', t('dash.history.period30d'))}
    ${periodPill('all', t('dash.history.periodAll'))}
  </div>
  <label class="dash-analytics-picker" data-history-picker style="display:none">
    <span class="label" data-i18n="dash.analytics.pickerLabel">${escapeHtml(t('dash.analytics.pickerLabel'))}</span>
    <select class="dash-analytics-picker__select" data-history-esim-select>
      <!-- options populated dynamically when fetchEsims completes -->
    </select>
  </label>
</div>
<div class="dash-history__filters" data-history-filters>
  ${catPill('all',    t('dash.history.filterAll'))}
  ${catPill('topup',  t('dash.history.filterTopup'))}
  ${catPill('usage',  t('dash.history.filterUsage'))}
  ${catPill('refund', t('dash.history.filterRefund'))}
  ${catPill('bonus',  t('dash.history.filterBonus'))}
</div>
<table class="dash-table dash-table--history" data-history-table>
  <thead><tr>
    <th data-i18n="dash.history.colDate">${escapeHtml(t('dash.history.colDate'))}</th>
    <th data-i18n="dash.history.colEsim">${escapeHtml(t('dash.history.colEsim'))}</th>
    <th data-i18n="dash.history.colType">${escapeHtml(t('dash.history.colType'))}</th>
    <th data-i18n="dash.history.colAmount">${escapeHtml(t('dash.history.colAmount'))}</th>
    <th data-i18n="dash.history.colDetail">${escapeHtml(t('dash.history.colDetail'))}</th>
  </tr></thead>
  <tbody data-history-rows>
    <tr><td colspan="5" class="dash-loading" style="border:0;" data-i18n="dash.history.loading">${escapeHtml(t('dash.history.loading'))}</td></tr>
  </tbody>
</table>
<p class="dash-sub" data-history-empty hidden data-i18n="dash.history.empty">${escapeHtml(t('dash.history.empty'))}</p>
<div class="dash-history__loadmore-wrap" data-history-loadmore-wrap hidden>
  <button class="btn btn--ghost btn--sm" type="button" data-history-loadmore data-i18n="dash.history.loadMore">${escapeHtml(t('dash.history.loadMore'))}</button>
</div>`;
}

/** Module-level state for the history view: period selection + cursor.
 *  `esimId` is the picker selection — empty string means "all eSIMs"
 *  (mirrors the analytics tab convention). */
const historyState = {
  period: 'all' as HistoryPeriod,
  cursor: null as string | null,
  activeCat: 'all' as HistoryCategory,
  esimId: '' as string,
};

/**
 * Map ISO-2 country codes to flag emoji. Pure function, no external lookup —
 * each ISO-2 char + 0x1F1A5 (regional indicator base offset) is the trick.
 */
export function flagFromIso(iso: string | null | undefined): string {
  if (!iso || iso.length !== 2) return '🌐';
  const A = 0x1F1E6;  // regional indicator A
  return String.fromCodePoint(...iso.toUpperCase().split('').map((c) => A + (c.charCodeAt(0) - 65)));
}

/**
 * Format human-readable detail from a ledger row, replacing the previous
 * `notes ?? refType ?? type` fallback that leaked raw JSON into the UI.
 */
export function formatHistoryDetail(e: api.LedgerEntryResp): string {
  // Usage: notes JSON → "🇷🇺 RU · 20 MB"
  if (e.type === 'usage' || e.type === 'esim-debit') {
    let mb: number | null = null;
    if (e.notes) {
      try {
        const parsed = JSON.parse(e.notes) as { deltaMb?: number };
        if (typeof parsed.deltaMb === 'number') mb = parsed.deltaMb;
      } catch { /* malformed notes — falls through */ }
    }
    const flag = flagFromIso(e.countryCode);
    const cc = e.countryCode ? escapeHtml(e.countryCode.toUpperCase()) : t('dash.history.usageGlobal');
    return mb !== null ? `${flag} ${cc} · ${fmtData(mb)}` : `${flag} ${cc}`;
  }
  // Topup / refund / bonus — humanise based on ref / promo / order id.
  if (e.type === 'topup') {
    return escapeHtml(t('dash.history.detailTopup'));
  }
  if (e.type === 'topup-refund' || e.type === 'refund') {
    return escapeHtml(t('dash.history.detailRefund'));
  }
  // Both used to fall through to the generic branch: the free first eSIM
  // read "Promo · —" and a referral transfer showed its raw ledger code.
  if (e.type === 'trial-credit') {
    return escapeHtml(t('dash.history.detailTrial'));
  }
  if (e.type === 'referral-credit') {
    return escapeHtml(t('dash.history.detailReferral'));
  }
  if (e.type === 'promo-bonus') {
    // Notes for promo-bonus typically contain the code. Best effort parse.
    if (e.notes) {
      const m = e.notes.match(/[A-Z][A-Z0-9]{2,31}/);
      if (m) return `${escapeHtml(t('dash.history.detailBonus'))} · <span class="mono">${escapeHtml(m[0])}</span>`;
    }
    return escapeHtml(t('dash.history.detailBonus'));
  }
  // Fallback: short, non-JSON. Strips obvious JSON-y starts so we don't
  // ever leak `{"deltaBytes":...` even for unknown types.
  const safe = (e.notes && !e.notes.startsWith('{') ? e.notes : null) ?? e.refType ?? '—';
  return escapeHtml(safe);
}

export function formatHistoryType(rawType: string): string {
  // User-facing label for each ledger type. Still respects i18n.
  const cat = getHistoryCategory(rawType);
  if (cat === 'topup') return escapeHtml(t('dash.history.typeTopup'));
  if (cat === 'usage') return escapeHtml(t('dash.history.typeUsage'));
  if (cat === 'refund') return escapeHtml(t('dash.history.typeRefund'));
  if (cat === 'bonus') return escapeHtml(t('dash.history.typeBonus'));
  // Referral earnings moved onto an eSIM. Deliberately not a filter category
  // (it is neither a top-up nor a promo), so it shows under "All" only.
  if (rawType === 'referral-credit') return escapeHtml(t('dash.history.typeReferral'));
  return escapeHtml(rawType);  // unknown / adjustment
}

/**
 * Render one row of HTML for a ledger entry. Pulled out so the load-more
 * append path can reuse it without re-mapping eSIM lookups every time.
 */
export function renderHistoryRow(e: api.LedgerEntryResp, esimById: Map<string, api.EsimResp>): string {
  const cls = e.amountMicroUsdt >= 0 ? 'is-positive' : 'is-negative';
  const cat = getHistoryCategory(e.type) ?? 'all';
  const linkedEsim = e.esimId ? esimById.get(e.esimId) : undefined;
  const esimIdText = linkedEsim
    ? (linkedEsim.msisdn ?? linkedEsim.displayId ?? linkedEsim.id.slice(-10))
    : null;
  const esimLabel = esimIdText
    ? `<span class="mono dash-history__esim-id">${escapeHtml(esimIdText)}</span>`
    : '<span class="dash-sub">—</span>';
  return `<tr data-history-cat="${cat}">
    <td class="mono">${fmtDate(e.createdAt)}</td>
    <td>${esimLabel}</td>
    <td><span class="dash-history__type-pill dash-history__type-pill--${cat}">${formatHistoryType(e.type)}</span></td>
    <td class="mono ${cls}">${fmtMoney(e.amountMicroUsdt, { signed: true })}</td>
    <td>${formatHistoryDetail(e)}</td>
  </tr>`;
}

/** Bump per-category count badges. Pass a delta map; positive numbers add. */
export function bumpHistoryCounts(scope: ParentNode, delta: Partial<Record<HistoryCategory, number>>): void {
  for (const cat of HISTORY_CATEGORIES) {
    const el = scope.querySelector<HTMLElement>(`[data-history-filter-count="${cat}"]`);
    if (!el) continue;
    const cur = parseInt((el.textContent ?? '').replace(/[^\d]/g, ''), 10) || 0;
    const next = cur + (delta[cat] ?? 0);
    el.textContent = `(${next})`;
  }
}

export async function loadHistoryInto(rows: HTMLElement, emptyEl: HTMLElement | null): Promise<void> {
  // Re-fetch from scratch with the current `historyState.period` (and
  // optional `historyState.esimId` when the user picked a single eSIM
  // from the dropdown). Cursor is reset because the result set changed.
  historyState.cursor = null;
  try {
    const [history, esimList] = await Promise.all([
      historyState.esimId
        ? api.fetchEsimHistory(historyState.esimId, { period: historyState.period, limit: 30 })
        : api.fetchHistoryWithPeriod({ period: historyState.period, limit: 30 }),
      api.fetchEsims(),
    ]);
    const root = rows.closest('section, div, body') ?? document;
    // Populate the eSIM picker — visible only when the user owns 2+ eSIMs.
    // Single-eSIM users don't need the filter; mirrors the analytics tab.
    const pickerWrap = root.querySelector<HTMLElement>('[data-history-picker]');
    const pickerSelect = root.querySelector<HTMLSelectElement>('[data-history-esim-select]');
    if (pickerWrap && pickerSelect && esimList.esims.length >= 2) {
      const allOpt = `<option value="">${escapeHtml(t('dash.analytics.pickerAll'))} (${esimList.esims.length})</option>`;
      const opts = esimList.esims.map((e) => {
        const idLabel = e.msisdn ?? e.displayId ?? e.id.slice(-8).toUpperCase();
        const status = e.status === 'reserved' ? ` · ${t('dash.esims.statusReserved')}` : '';
        return `<option value="${escapeHtml(e.id)}">#${escapeHtml(idLabel)}${escapeHtml(status)}</option>`;
      }).join('');
      pickerSelect.innerHTML = allOpt + opts;
      pickerSelect.value = historyState.esimId;
      pickerWrap.style.display = 'inline-flex';
    } else if (pickerWrap) {
      pickerWrap.style.display = 'none';
      // If the user previously had a picker selection but the eSIM list
      // shrank (e.g. an eSIM was archived), clear the state so we don't
      // keep filtering on a now-hidden dropdown.
      if (historyState.esimId) historyState.esimId = '';
    }
    // Reset count badges before appending fresh data.
    for (const cat of HISTORY_CATEGORIES) {
      const el = root.querySelector<HTMLElement>(`[data-history-filter-count="${cat}"]`);
      if (el) el.textContent = '(0)';
    }
    if (history.entries.length === 0) {
      rows.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      const lm = root.querySelector<HTMLElement>('[data-history-loadmore-wrap]');
      if (lm) lm.hidden = true;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;
    const esimById = new Map(esimList.esims.map((e) => [e.id, e]));
    rows.innerHTML = history.entries.map((e) => renderHistoryRow(e, esimById)).join('');

    const delta: Partial<Record<HistoryCategory, number>> = { all: 0, topup: 0, usage: 0, refund: 0, bonus: 0 };
    for (const e of history.entries) {
      delta.all = (delta.all ?? 0) + 1;
      const c = getHistoryCategory(e.type);
      if (c) delta[c] = (delta[c] ?? 0) + 1;
    }
    bumpHistoryCounts(root, delta);

    historyState.cursor = history.nextCursor;
    const lm = root.querySelector<HTMLElement>('[data-history-loadmore-wrap]');
    if (lm) lm.hidden = !history.nextCursor;

    // Re-apply the active category filter to the freshly rendered rows.
    applyHistoryCategoryFilter(rows, historyState.activeCat);
  } catch (err) {
    rows.innerHTML = `<tr><td colspan="5">${escapeHtml(t('dash.history.errorLoad'))} ${escapeHtml((err as Error).message)}</td></tr>`;
  }
}

/** Append the next page of entries using the saved cursor. */
export async function loadMoreHistoryInto(rows: HTMLElement): Promise<void> {
  if (!historyState.cursor) return;
  const root = rows.closest('section, div, body') ?? document;
  const btn = root.querySelector<HTMLButtonElement>('[data-history-loadmore]');
  if (btn) { btn.disabled = true; btn.textContent = t('dash.history.loading'); }
  try {
    const [history, esimList] = await Promise.all([
      historyState.esimId
        ? api.fetchEsimHistory(historyState.esimId, { period: historyState.period, cursor: historyState.cursor, limit: 30 })
        : api.fetchHistoryWithPeriod({ period: historyState.period, cursor: historyState.cursor, limit: 30 }),
      api.fetchEsims(),
    ]);
    const esimById = new Map(esimList.esims.map((e) => [e.id, e]));
    const nextHtml = history.entries.map((e) => renderHistoryRow(e, esimById)).join('');
    rows.insertAdjacentHTML('beforeend', nextHtml);
    const delta: Partial<Record<HistoryCategory, number>> = { all: 0, topup: 0, usage: 0, refund: 0, bonus: 0 };
    for (const e of history.entries) {
      delta.all = (delta.all ?? 0) + 1;
      const c = getHistoryCategory(e.type);
      if (c) delta[c] = (delta[c] ?? 0) + 1;
    }
    bumpHistoryCounts(root, delta);
    historyState.cursor = history.nextCursor;
    const lm = root.querySelector<HTMLElement>('[data-history-loadmore-wrap]');
    if (lm) lm.hidden = !history.nextCursor;
    applyHistoryCategoryFilter(rows, historyState.activeCat);
  } catch (err) {
    if (btn) { btn.disabled = false; btn.textContent = t('dash.history.loadMore'); }
    showToast(`${t('dash.history.errorLoad')} ${(err as Error).message}`, 'error');
    return;
  }
  if (btn) { btn.disabled = false; btn.textContent = t('dash.history.loadMore'); }
}

/** Apply the in-memory category filter to all currently rendered rows. */
export function applyHistoryCategoryFilter(rows: HTMLElement, cat: HistoryCategory): void {
  rows.querySelectorAll<HTMLElement>('tr[data-history-cat]').forEach((tr) => {
    const visible = cat === 'all' || tr.dataset.historyCat === cat;
    tr.style.display = visible ? '' : 'none';
  });
}

/**
 * Wire all history-tab interactivity: category pills, period pills, and
 * the Load-more button. Call once after the panel mounts. Returns a
 * combined cleanup that removes every listener.
 */
export function wireHistoryFilters(scope: HTMLElement): (() => void) | null {
  const catFilters = scope.querySelector<HTMLElement>('[data-history-filters]');
  const periodFilters = scope.querySelector<HTMLElement>('[data-history-period-filters]');
  const esimSelect = scope.querySelector<HTMLSelectElement>('[data-history-esim-select]');
  const rows = scope.querySelector<HTMLElement>('[data-history-rows]');
  const emptyEl = scope.querySelector<HTMLElement>('[data-history-empty]');
  const loadMoreBtn = scope.querySelector<HTMLElement>('[data-history-loadmore]');
  if (!catFilters && !periodFilters && !esimSelect && !loadMoreBtn) return null;

  const offs: Array<() => void> = [];

  // Category pills — pure client-side row toggle.
  if (catFilters) {
    const onCatClick = (ev: Event): void => {
      const btn = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-history-filter]');
      if (!btn) return;
      const cat = btn.dataset.historyFilter as HistoryCategory | undefined;
      if (!cat) return;
      historyState.activeCat = cat;
      catFilters.querySelectorAll<HTMLElement>('[data-history-filter]').forEach((b) => {
        b.classList.toggle('is-active', b === btn);
      });
      if (rows) applyHistoryCategoryFilter(rows, cat);
    };
    catFilters.addEventListener('click', onCatClick);
    offs.push(() => catFilters.removeEventListener('click', onCatClick));
  }

  // Period pills — re-fetch from server with the new ?period=.
  if (periodFilters && rows) {
    const onPeriodClick = (ev: Event): void => {
      const btn = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-history-period]');
      if (!btn) return;
      const p = btn.dataset.historyPeriod as HistoryPeriod | undefined;
      if (!p || p === historyState.period) return;
      historyState.period = p;
      periodFilters.querySelectorAll<HTMLElement>('[data-history-period]').forEach((b) => {
        b.classList.toggle('is-active', b === btn);
      });
      void loadHistoryInto(rows, emptyEl);
    };
    periodFilters.addEventListener('click', onPeriodClick);
    offs.push(() => periodFilters.removeEventListener('click', onPeriodClick));
  }

  // eSIM picker — switch the fetcher between consolidated /api/me/history
  // and per-eSIM /api/esims/:id/history. Empty value = "all eSIMs".
  if (esimSelect && rows) {
    const onEsimChange = (): void => {
      const next = esimSelect.value;
      if (next === historyState.esimId) return;
      historyState.esimId = next;
      void loadHistoryInto(rows, emptyEl);
    };
    esimSelect.addEventListener('change', onEsimChange);
    offs.push(() => esimSelect.removeEventListener('change', onEsimChange));
  }

  // Load more — append next page.
  if (loadMoreBtn && rows) {
    const onLoadMore = (): void => { void loadMoreHistoryInto(rows); };
    loadMoreBtn.addEventListener('click', onLoadMore);
    offs.push(() => loadMoreBtn.removeEventListener('click', onLoadMore));
  }

  return () => { for (const off of offs) off(); };
}

/* ─── Support, Settings ───────── */
